/**
 * Chatbot Auth Utilities
 * Handles authentication state detection and session management
 * Provides methods to track user login/logout state
 */

/**
 * Get current authenticated user from localStorage
 * @returns {Object|null} User object or null if not authenticated
 */
export const getCurrentUser = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

/**
 * Check if user is currently authenticated
 * @returns {boolean} True if user is logged in
 */
export const isUserLoggedIn = () => {
  const user = getCurrentUser();
  return user && user.user_id ? true : false;
};

/**
 * Get user ID from current session
 * @returns {number|null} User ID or null if not authenticated
 */
export const getUserId = () => {
  const user = getCurrentUser();
  return user?.user_id ? parseInt(user.user_id, 10) : null;
};

/**
 * Initialize auth state listener
 * Calls callback whenever authentication state changes
 * @param {Function} callback - Function to call on auth state change (args: isLoggedIn, user)
 * @returns {Function} Unsubscribe function
 */
export const listenToAuthChanges = (callback) => {
  // Get initial state
  const initialUser = getCurrentUser();
  const initialLoggedIn = isUserLoggedIn();
  
  let lastUser = initialUser;

  // Create an interval-based check for storage changes
  // (storage event is unreliable across same tab)
  const checkInterval = setInterval(() => {
    const currentUser = getCurrentUser();
    const currentLoggedIn = isUserLoggedIn();

    // Check if state changed
    if (
      JSON.stringify(lastUser) !== JSON.stringify(currentUser) ||
      currentLoggedIn !== (lastUser !== null)
    ) {
      lastUser = currentUser;
      callback(currentLoggedIn, currentUser);
    }
  }, 500); // Check every 500ms

  // Return unsubscribe function
  return () => {
    clearInterval(checkInterval);
  };
};

/**
 * Listen for logout event
 * Detects when user is removed from localStorage
 * @param {Function} callback - Function to call on logout
 * @returns {Function} Unsubscribe function
 */
export const listenToLogout = (callback) => {
  let wasLoggedIn = isUserLoggedIn();

  const checkInterval = setInterval(() => {
    const isLoggedIn = isUserLoggedIn();

    // Detect logout (was logged in, now not logged in)
    if (wasLoggedIn && !isLoggedIn) {
      wasLoggedIn = false;
      callback();
    } else if (!wasLoggedIn && isLoggedIn) {
      wasLoggedIn = true;
    }
  }, 500);

  return () => {
    clearInterval(checkInterval);
  };
};

/**
 * Listen for login event
 * Detects when user is added to localStorage
 * @param {Function} callback - Function to call on login (args: user)
 * @returns {Function} Unsubscribe function
 */
export const listenToLogin = (callback) => {
  let wasLoggedIn = isUserLoggedIn();

  const checkInterval = setInterval(() => {
    const user = getCurrentUser();
    const isLoggedIn = isUserLoggedIn();

    // Detect login (was not logged in, now logged in)
    if (!wasLoggedIn && isLoggedIn) {
      wasLoggedIn = true;
      callback(user);
    } else if (wasLoggedIn && !isLoggedIn) {
      wasLoggedIn = false;
    }
  }, 500);

  return () => {
    clearInterval(checkInterval);
  };
};

/**
 * Clear all chatbot-related session data
 * Used when user logs out or session ends
 */
export const clearChatbotSessionData = () => {
  // Clear localStorage chat state
  localStorage.removeItem('liveSupportTicketId');
  localStorage.removeItem('liveSupportEscalated');
  localStorage.removeItem('chatbotMessages'); // If we store messages locally
  localStorage.removeItem('chatbotSession');
};

/**
 * Clear all chatbot session data from sessionStorage
 * For guest sessions that should not persist
 */
export const clearGuestSessionData = () => {
  sessionStorage.removeItem('guestChatbotTicketId');
  sessionStorage.removeItem('guestChatbotEscalated');
};

export default {
  getCurrentUser,
  isUserLoggedIn,
  getUserId,
  listenToAuthChanges,
  listenToLogout,
  listenToLogin,
  clearChatbotSessionData,
  clearGuestSessionData,
};
